import type { EChartsOption } from 'echarts'

// Light mode themes
// Soft Cosmic Flare theme - Light Mode
export const softCosmicFlareThemeLight: EChartsOption = {
  color: [
    '#8B5CF6', // Bolder Purple
    '#6366F1', // Bolder Indigo
    '#3B82F6', // Bolder Blue
    '#10B981', // Bolder Emerald
    '#EC4899', // Bolder Pink
  ],
  backgroundColor: 'transparent',
  textStyle: {
    color: '#4B5563', // Darker Gray for better contrast
    fontWeight: 500
  },
  title: {
    textStyle: {
      color: '#1F2937', // Even darker gray for title
      fontWeight: 600,
      fontSize: 16
    },
  },
  legend: {
    textStyle: {
      color: '#374151', // Darker gray for legend
      fontWeight: 500
    },
  },
  axis: {
    axisLine: {
      lineStyle: {
        color: '#9CA3AF', // Bolder axis lines
        width: 2
      },
    },
    axisTick: {
      lineStyle: {
        color: '#9CA3AF', // Matching axis ticks
        width: 2
      },
      length: 6
    },
    axisLabel: {
      color: '#4B5563', // Darker labels
      fontWeight: 500
    },
    splitLine: {
      lineStyle: {
        color: ['#E5E7EB'], // Slightly darker grid lines
        width: 1.5
      },
    },
  },
  series: [
    {
      type: 'pie',
      label: {
        color: '#1F2937', // Dark text for light mode
        fontWeight: 'bold',
        textBorderColor: 'rgba(255, 255, 255, 0.8)', // Light border for contrast
        textBorderWidth: 2,
        textShadowBlur: 2,
        textShadowColor: 'rgba(255, 255, 255, 0.8)'
      }
    }
  ]
}

// Gentle Photon Stream theme - Light Mode
export const gentlePhotonStreamThemeLight: EChartsOption = {
  color: [
    '#059669', // Bolder Emerald
    '#0891B2', // Bolder Cyan
    '#4F46E5', // Bolder Indigo
    '#9333EA', // Bolder Purple
    '#0284C7', // Bolder Sky
  ],
  backgroundColor: 'transparent',
  textStyle: {
    color: '#4B5563',
    fontWeight: 500
  },
  title: {
    textStyle: {
      color: '#1F2937',
      fontWeight: 600,
      fontSize: 16
    },
  },
  legend: {
    textStyle: {
      color: '#374151',
      fontWeight: 500
    },
  },
  axis: {
    axisLine: {
      lineStyle: {
        color: '#9CA3AF',
        width: 2
      },
    },
    axisTick: {
      lineStyle: {
        color: '#9CA3AF',
        width: 2
      },
      length: 6
    },
    axisLabel: {
      color: '#4B5563',
      fontWeight: 500
    },
    splitLine: {
      lineStyle: {
        color: ['#E5E7EB'],
        width: 1.5
      },
    },
  },
}

// Pastel Azure theme - Light Mode
export const pastelAzureThemeLight: EChartsOption = {
  color: [
    '#0EA5E9', // Bolder Sky
    '#2563EB', // Bolder Blue
    '#059669', // Bolder Emerald
    '#0891B2', // Bolder Cyan
    '#4F46E5', // Bolder Indigo
  ],
  backgroundColor: 'transparent',
  textStyle: {
    color: '#4B5563',
    fontWeight: 500
  },
  title: {
    textStyle: {
      color: '#1F2937',
      fontWeight: 600,
      fontSize: 16
    },
  },
  legend: {
    textStyle: {
      color: '#374151',
      fontWeight: 500
    },
  },
  axis: {
    axisLine: {
      lineStyle: {
        color: '#9CA3AF',
        width: 2
      },
    },
    axisTick: {
      lineStyle: {
        color: '#9CA3AF',
        width: 2
      },
      length: 6
    },
    axisLabel: {
      color: '#4B5563',
      fontWeight: 500
    },
    splitLine: {
      lineStyle: {
        color: ['#E5E7EB'],
        width: 1.5
      },
    },
  },
}

// Dark mode themes
// Soft Cosmic Flare theme - Dark Mode
export const softCosmicFlareThemeDark: EChartsOption = {
  color: [
    '#A78BFA', // Lighter Purple
    '#818CF8', // Lighter Indigo
    '#60A5FA', // Lighter Blue
    '#34D399', // Lighter Emerald
    '#F472B6', // Lighter Pink
  ],
  backgroundColor: 'transparent',
  textStyle: {
    color: '#D1D5DB', // Light Gray for better contrast in dark mode
    fontWeight: 500
  },
  title: {
    textStyle: {
      color: '#F3F4F6', // Very light gray for title in dark mode
      fontWeight: 600,
      fontSize: 16
    },
  },
  legend: {
    textStyle: {
      color: '#E5E7EB', // Light gray for legend in dark mode
      fontWeight: 500
    },
  },
  axis: {
    axisLine: {
      lineStyle: {
        color: 'rgba(107, 114, 128, 0.3)', // Much more subtle axis lines for dark mode
        width: 1
      },
    },
    axisTick: {
      lineStyle: {
        color: 'rgba(107, 114, 128, 0.3)', // Matching axis ticks
        width: 1
      },
      length: 4
    },
    axisLabel: {
      color: '#D1D5DB', // Light labels for dark mode
      fontWeight: 500
    },
    splitLine: {
      show: false
    },
  },
  series: [
    {
      type: 'pie',
      label: {
        color: '#FFFFFF', // Pure white for maximum visibility
        fontWeight: 'bold',
        textBorderColor: 'rgba(0, 0, 0, 0.9)', // Very dark border for maximum contrast
        textBorderWidth: 4, // Thicker border
        textShadowBlur: 4, // Stronger blur
        textShadowColor: 'rgba(0, 0, 0, 0.9)' // Darker shadow
      }
    }
  ]
}

// Gentle Photon Stream theme - Dark Mode
export const gentlePhotonStreamThemeDark: EChartsOption = {
  color: [
    '#34D399', // Lighter Emerald
    '#22D3EE', // Lighter Cyan
    '#818CF8', // Lighter Indigo
    '#A78BFA', // Lighter Purple
    '#38BDF8', // Lighter Sky
  ],
  backgroundColor: 'transparent',
  textStyle: {
    color: '#D1D5DB',
    fontWeight: 500
  },
  title: {
    textStyle: {
      color: '#F3F4F6',
      fontWeight: 600,
      fontSize: 16
    },
  },
  legend: {
    textStyle: {
      color: '#E5E7EB',
      fontWeight: 500
    },
  },
  axis: {
    axisLine: {
      lineStyle: {
        color: 'rgba(107, 114, 128, 0.3)', // Much more subtle axis lines for dark mode
        width: 1
      },
    },
    axisTick: {
      lineStyle: {
        color: 'rgba(107, 114, 128, 0.3)', // Matching axis ticks
        width: 1
      },
      length: 4
    },
    axisLabel: {
      color: '#D1D5DB',
      fontWeight: 500
    },
    splitLine: {
      show: false
    },
  },
  series: [
    {
      type: 'pie',
      label: {
        color: '#FFFFFF', // White text for pie chart labels
        fontWeight: 500,
        textBorderColor: 'rgba(0, 0, 0, 0.3)', // Subtle dark border for better contrast
        textBorderWidth: 2,
        textShadowBlur: 2,
        textShadowColor: 'rgba(0, 0, 0, 0.5)'
      }
    }
  ]
}

// Pastel Azure theme - Dark Mode
export const pastelAzureThemeDark: EChartsOption = {
  color: [
    '#38BDF8', // Lighter Sky
    '#60A5FA', // Lighter Blue
    '#34D399', // Lighter Emerald
    '#22D3EE', // Lighter Cyan
    '#818CF8', // Lighter Indigo
  ],
  backgroundColor: 'transparent',
  textStyle: {
    color: '#D1D5DB',
    fontWeight: 500
  },
  title: {
    textStyle: {
      color: '#F3F4F6',
      fontWeight: 600,
      fontSize: 16
    },
  },
  legend: {
    textStyle: {
      color: '#E5E7EB',
      fontWeight: 500
    },
  },
  axis: {
    axisLine: {
      lineStyle: {
        color: 'rgba(107, 114, 128, 0.3)', // Much more subtle axis lines for dark mode
        width: 1
      },
    },
    axisTick: {
      lineStyle: {
        color: 'rgba(107, 114, 128, 0.3)', // Matching axis ticks
        width: 1
      },
      length: 4
    },
    axisLabel: {
      color: '#D1D5DB',
      fontWeight: 500
    },
    splitLine: {
      show: false
    },
  },
  series: [
    {
      type: 'pie',
      label: {
        color: '#FFFFFF', // White text for pie chart labels
        fontWeight: 500,
        textBorderColor: 'rgba(0, 0, 0, 0.3)', // Subtle dark border for better contrast
        textBorderWidth: 2,
        textShadowBlur: 2,
        textShadowColor: 'rgba(0, 0, 0, 0.5)'
      }
    }
  ]
}

// Aliases for backward compatibility
export const softCosmicFlareTheme = softCosmicFlareThemeLight;
export const gentlePhotonStreamTheme = gentlePhotonStreamThemeLight;
export const pastelAzureTheme = pastelAzureThemeLight;

// Default theme for TabLab
export const defaultThemeLight = softCosmicFlareThemeLight;
export const defaultThemeDark = softCosmicFlareThemeDark;
export const defaultTheme = defaultThemeLight; 